import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Package, Plus, Pencil, Trash2, AlertTriangle, Download, HelpCircle, History, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "../lib/csv";
import { computeDaysLeft, formatDaysLeft } from "../lib/vials";
import InfoTip from "../components/InfoTip";
import ProductTour from "../components/ProductTour";

const VIAL_TOUR_KEY = "amino_vial_tour_seen_v1";
const VIAL_TOUR_STEPS = [
  {
    placement: "center",
    title: "How to add a vial",
    body: "Vials auto-deduct as you log shots (FIFO). Get these fields right and everything else takes care of itself.",
  },
  {
    selector: '[data-testid="vial-peptide"]',
    placement: "bottom",
    title: "1. Which peptide?",
    body: "Pick from your library. Every shot you log for this peptide will drain from the oldest matching vial first.",
  },
  {
    selector: '[data-testid="vial-amount-mg"]',
    placement: "bottom",
    title: "2. Amount (mg)",
    body: "The total milligrams in the vial (the label number — usually 5 mg or 10 mg). This becomes your starting `remaining_mg`.",
  },
  {
    selector: '[data-testid="vial-bac-ml"]',
    placement: "bottom",
    title: "3. BAC water (mL)",
    body: "How much bacteriostatic water you reconstituted with. Combined with the mg amount, the Calculator uses this to compute concentration + units per dose.",
  },
  {
    selector: '[data-testid="vial-purity"]',
    placement: "top",
    title: "4. Purity %",
    body: "Optional. Default is 100. Lower this if your COA reports below-spec purity — the Calculator will scale doses accordingly.",
  },
  {
    selector: '[data-testid="vial-expiration"]',
    placement: "top",
    title: "5. Expiration date",
    body: "Optional but recommended. The Dashboard flags vials expiring within 30 days so you can plan a replacement.",
  },
  {
    selector: '[data-testid="vial-save"]',
    placement: "top",
    title: "6. Save",
    body: "The vial appears in Inventory and becomes eligible for auto-deduction on every future shot. Done!",
  },
];

const empty = { peptide_name: "", amount_mg: "", bac_water_ml: "", purity_pct: "100", expiration_date: "", quantity: "1", notes: "" };

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function fmtOpenTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const daysAgo = Math.floor((now - d) / 86400000);
    const timeStr = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    if (sameDay) return `Today · ${timeStr}`;
    if (daysAgo < 7) return `${d.toLocaleDateString(undefined, { weekday: "short" })} · ${timeStr}`;
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${timeStr}`;
  } catch { return String(iso); }
}

function isExpiringSoon(iso) {
  if (!iso) return false;
  const days = (new Date(iso).getTime() - Date.now()) / 86400000;
  return days < 30 && days >= 0;
}
function isExpired(iso) {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

export default function Inventory() {
  const [vials, setVials] = useState([]);
  const [peptides, setPeptides] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  // Mobile-only sort: keeps long vial lists scannable on phones.
  // Persisted per-device so the user's preferred sort survives page refresh.
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem("amino_inventory_sort_v1") || "expiring_soon"; }
    catch { return "expiring_soon"; }
  });
  useEffect(() => {
    try { localStorage.setItem("amino_inventory_sort_v1", sortKey); } catch { /* ignore */ }
  }, [sortKey]);

  // Mobile-only peptide filter — narrows the card list to a single peptide when
  // the user has a stash of the same one across multiple vials. "all" shows every vial.
  const [peptideFilter, setPeptideFilter] = useState(() => {
    try { return localStorage.getItem("amino_inventory_peptide_filter_v1") || "all"; }
    catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("amino_inventory_peptide_filter_v1", peptideFilter); } catch { /* ignore */ }
  }, [peptideFilter]);

  // Unique peptide names currently in the vault, sorted A→Z with counts.
  const peptideOptions = useMemo(() => {
    const counts = {};
    vials.forEach((v) => {
      const n = v.peptide_name || "Unknown";
      counts[n] = (counts[n] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({ name, count }));
  }, [vials]);

  // If the currently-selected peptide vanishes (e.g. all vials of it were deleted),
  // silently fall back to "all" so the empty-filter card never confuses the user.
  // Guard: only reset once vials have actually loaded, otherwise the initial
  // load flash wipes out the user's persisted choice.
  useEffect(() => {
    if (vials.length === 0) return; // not loaded yet
    if (peptideFilter !== "all" && !peptideOptions.some((p) => p.name === peptideFilter)) {
      setPeptideFilter("all");
    }
  }, [peptideOptions, peptideFilter, vials.length]);

  // Sorted + filtered vials for the mobile card view.
  const sortedVials = useMemo(() => {
    // Filter first, then sort — smaller array = cheaper sort.
    let arr = peptideFilter === "all"
      ? [...vials]
      : vials.filter((v) => (v.peptide_name || "Unknown") === peptideFilter);
    const daysLeftFor = (v) => {
      const pep = peptides.find((p) => (p.name || "").toLowerCase() === (v.peptide_name || "").toLowerCase());
      const f = computeDaysLeft(v, pep);
      return f ? f.days : Number.POSITIVE_INFINITY;
    };
    const expTs = (v) => {
      if (!v.expiration_date) return Number.POSITIVE_INFINITY;
      const t = Date.parse(v.expiration_date);
      return isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    if (sortKey === "peptide_name") {
      arr.sort((a, b) => (a.peptide_name || "").localeCompare(b.peptide_name || ""));
    } else if (sortKey === "days_left") {
      arr.sort((a, b) => daysLeftFor(a) - daysLeftFor(b));
    } else {
      arr.sort((a, b) => expTs(a) - expTs(b));
    }
    return arr;
  }, [vials, peptides, sortKey, peptideFilter]);

  // First-open auto-launch of the vial mini-tour (only when creating, not editing)
  useEffect(() => {
    if (!dialogOpen || editing) return;
    try {
      if (!localStorage.getItem(VIAL_TOUR_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, [dialogOpen, editing]);

  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(VIAL_TOUR_KEY, "1"); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    try {
      const [v, p] = await Promise.all([api.get("/vials"), api.get("/peptides")]);
      setVials(v.data);
      setPeptides(p.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };

  const openNextVial = async (v) => {
    // Move to the next sealed vial in the stash: quantity -= 1, remaining_mg = amount_mg,
    // opens_count += 1 so the ordinal (2nd/3rd/etc.) stays honest across manual + auto opens.
    const nextQty = Math.max(1, (Number(v.quantity) || 1) - 1);
    const nextOpens = (Number(v.opens_count) || 1) + 1;
    try {
      await api.put(`/vials/${v.id}`, {
        peptide_name: v.peptide_name,
        amount_mg: v.amount_mg,
        bac_water_ml: v.bac_water_ml,
        purity_pct: v.purity_pct,
        expiration_date: v.expiration_date,
        quantity: nextQty,
        remaining_mg: v.amount_mg,
        opens_count: nextOpens,
        notes: v.notes || "",
      });
      const ord = (n) => {
        const abs = Math.abs(Number(n) || 0);
        const m10 = abs % 10, m100 = abs % 100;
        return `${abs}${(m10 === 1 && m100 !== 11) ? "st" : (m10 === 2 && m100 !== 12) ? "nd" : (m10 === 3 && m100 !== 13) ? "rd" : "th"}`;
      };
      toast.success(`Opened your ${ord(nextOpens)} ${v.peptide_name} vial · ${nextQty - 1} sealed left`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      peptide_name: v.peptide_name,
      amount_mg: String(v.amount_mg ?? ""),
      bac_water_ml: v.bac_water_ml != null ? String(v.bac_water_ml) : "",
      purity_pct: v.purity_pct != null ? String(v.purity_pct) : "100",
      expiration_date: v.expiration_date ? new Date(v.expiration_date).toISOString().slice(0, 10) : "",
      quantity: String(v.quantity ?? "1"),
      notes: v.notes || "",
    });
    setDialogOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.peptide_name) { toast.error("Choose a peptide"); return; }
    setSaving(true);
    try {
      const payload = {
        peptide_name: form.peptide_name,
        amount_mg: parseFloat(form.amount_mg),
        bac_water_ml: form.bac_water_ml === "" ? null : parseFloat(form.bac_water_ml),
        purity_pct: form.purity_pct === "" ? 100 : parseFloat(form.purity_pct),
        expiration_date: form.expiration_date ? new Date(form.expiration_date + "T12:00:00").toISOString() : null,
        quantity: parseInt(form.quantity) || 1,
        notes: form.notes || null,
      };
      if (editing) {
        await api.put(`/vials/${editing.id}`, payload);
        toast.success("Vial updated");
      } else {
        await api.post("/vials", payload);
        toast.success("Vial added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    // Snapshot the vial before delete so an accidental tap is one-click recoverable.
    const snapshot = confirmDelete;
    try {
      await api.delete(`/vials/${snapshot.id}`);
      setConfirmDelete(null);
      load();
      toast(`Deleted vial: ${snapshot.peptide_name}`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/vials", {
                peptide_name: snapshot.peptide_name,
                amount_mg: snapshot.amount_mg,
                bac_water_ml: snapshot.bac_water_ml ?? null,
                purity_pct: snapshot.purity_pct ?? 100.0,
                expiration_date: snapshot.expiration_date || null,
                quantity: snapshot.quantity ?? 1,
                notes: snapshot.notes || null,
                remaining_mg: snapshot.remaining_mg ?? null,
              });
              load();
              toast.success("Restored");
            } catch (err) { toast.error(formatApiError(err)); }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppLayout>
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-[#0D9488]" />
            <div className="font-mono-num text-[11px] text-[#0D9488] uppercase tracking-[0.3em]">// Vault</div>
          </div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none">Inventory</h1>
          <p className="text-slate-500 mt-3 text-sm">Track your vials, quantities, and expiration dates. {vials.length} on hand.</p>
        </div>
        <Button onClick={openNew}
          className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-11 px-6 uppercase tracking-widest font-semibold"
          data-testid="add-vial-button">
          <Plus className="w-4 h-4 mr-2" /> Add Vial
        </Button>
      </div>

      <div className="mb-4 flex justify-end">
        <Button
          variant="outline"
          onClick={() => downloadCsv("/export/vials.csv", "amino-trade-vials.csv")}
          className="border-slate-200 bg-white hover:bg-slate-50 text-slate-800 rounded-none h-10 uppercase tracking-widest text-xs font-semibold"
          data-testid="inventory-export-csv"
        >
          <Download className="w-3.5 h-3.5 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-none" data-testid="inventory-table-card">
        {vials.length === 0 ? (
          <div className="p-16 text-center">
            <div className="font-display font-black text-3xl uppercase text-slate-500">No vials yet</div>
            <p className="text-slate-500 mt-2 text-sm">Add your first vial to start tracking inventory.</p>
          </div>
        ) : (
          <>
          {/* Mobile stacked-card view */}
          <div className="sm:hidden" data-testid="inventory-mobile-wrap">
            {/* Filter + sort controls — mobile-only, kept out of the desktop table */}
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 space-y-2">
              {peptideOptions.length > 1 && (
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="inventory-peptide-filter"
                    className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 shrink-0 w-10"
                  >
                    // Peptide
                  </label>
                  <select
                    id="inventory-peptide-filter"
                    value={peptideFilter}
                    onChange={(e) => setPeptideFilter(e.target.value)}
                    className="flex-1 h-9 rounded-none border border-slate-200 bg-white text-slate-900 text-sm px-2 focus:outline-none focus:border-[#0D9488]"
                    data-testid="inventory-peptide-filter"
                  >
                    <option value="all">All peptides ({vials.length})</option>
                    {peptideOptions.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.name} ({p.count})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <label
                  htmlFor="inventory-sort"
                  className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 shrink-0 w-10"
                >
                  // Sort
                </label>
                <select
                  id="inventory-sort"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  className="flex-1 h-9 rounded-none border border-slate-200 bg-white text-slate-900 text-sm px-2 focus:outline-none focus:border-[#0D9488]"
                  data-testid="inventory-sort-select"
                >
                  <option value="expiring_soon">Expiring soon</option>
                  <option value="days_left">Days left</option>
                  <option value="peptide_name">Peptide name (A–Z)</option>
                </select>
              </div>
            </div>
            {sortedVials.length === 0 ? (
              <div className="p-8 text-center" data-testid="inventory-mobile-empty-filter">
                <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  No {peptideFilter} vials
                </div>
                <button
                  type="button"
                  onClick={() => setPeptideFilter("all")}
                  className="font-mono-num text-[11px] uppercase tracking-widest text-[#0D9488] border border-[#0D9488]/40 px-3 py-1.5 hover:bg-[#0D9488]/10 transition-colors"
                  data-testid="inventory-clear-peptide-filter"
                >
                  Show all peptides
                </button>
              </div>
            ) : (
            <div className="divide-y divide-slate-200" data-testid="inventory-mobile-cards">
            {sortedVials.map((v) => {
              const expired = isExpired(v.expiration_date);
              const soon = !expired && isExpiringSoon(v.expiration_date);
              const hasExp = !!v.expiration_date;
              const pepInfo = peptides.find((p) => (p.name || "").toLowerCase() === (v.peptide_name || "").toLowerCase());
              const forecast = computeDaysLeft(v, pepInfo);
              const runOutDate = forecast && forecast.days > 0 ? new Date(Date.now() + forecast.days * 86400000) : null;
              const qty = Math.max(1, Number(v.quantity) || 1);
              const activeVialFraction = v.remaining_mg != null && v.amount_mg
                ? Math.max(0, Math.min(1, v.remaining_mg / v.amount_mg))
                : 1;
              // "Open next vial" shows when the active vial is partially/fully used AND there are sealed spares.
              const canOpenNext = qty > 1 && v.remaining_mg != null && v.remaining_mg < (v.amount_mg || 0);
              const daysColor = !forecast
                ? "text-slate-500"
                : forecast.days <= 0 ? "text-[#FF3B30]"
                : forecast.days < 3   ? "text-[#FF3B30]"
                : forecast.days < 7   ? "text-[#0D9488]"
                : "text-slate-900";
              const barColor = !forecast
                ? "bg-zinc-800"
                : forecast.days <= 0 ? "bg-[#FF3B30]"
                : forecast.days < 7  ? "bg-[#0D9488]"
                : "bg-[#22C55E]";
              const pct = v.remaining_mg != null
                ? Math.max(0, Math.min(100, (v.remaining_mg / v.amount_mg) * 100))
                : null;
              return (
                <div key={v.id} className="p-4" data-testid={`vial-card-${v.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-black text-lg text-slate-900 uppercase tracking-tight leading-tight truncate">
                        {v.peptide_name}
                      </div>
                      <div className="font-mono-num text-[11px] text-slate-500 mt-0.5 tabular-nums">
                        {v.amount_mg} mg per vial · {qty} on hand
                        {qty > 1 && (
                          <span className="text-slate-400"> ({Math.round(activeVialFraction * 100)}% of active · {qty - 1} sealed)</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(v)}
                        className="h-9 w-9 hover:bg-slate-100 text-slate-600 rounded-none"
                        data-testid={`edit-vial-${v.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(v)}
                        className="h-9 w-9 hover:bg-slate-100 text-slate-600 hover:text-[#FF3B30] rounded-none"
                        data-testid={`delete-vial-${v.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Two side-by-side stats: Remaining + Days Left */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500 mb-1">Remaining</div>
                      {v.remaining_mg != null ? (
                        <>
                          <div className={`font-mono-num text-sm tabular-nums ${v.remaining_mg <= 0 ? "text-[#FF3B30]" : v.remaining_mg / v.amount_mg < 0.25 ? "text-[#0D9488]" : "text-slate-900"}`}>
                            {v.remaining_mg.toFixed(2)} mg
                          </div>
                          <div className="h-1 w-full bg-slate-100 mt-1">
                            <div
                              className={`h-full ${v.remaining_mg <= 0 ? "bg-[#FF3B30]" : v.remaining_mg / v.amount_mg < 0.25 ? "bg-[#0D9488]" : "bg-[#2563EB]"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="text-slate-500 font-mono-num text-sm">—</div>
                      )}
                    </div>
                    <div data-testid={`vial-days-left-${v.id}`}>
                      <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500 mb-1 inline-flex items-center gap-1">
                        Days Left <InfoTip text="Estimated days of therapy remaining at the peptide's default dose + interval." />
                      </div>
                      {!forecast ? (
                        <div className="text-slate-400 font-mono-num text-sm">n/a</div>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-1.5">
                            <span className={`font-mono-num text-sm font-black ${daysColor}`}>{formatDaysLeft(forecast.days)}</span>
                            {runOutDate && forecast.days >= 0.5 && (
                              <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">
                                {runOutDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                              </span>
                            )}
                          </div>
                          <div className="h-1 w-full bg-slate-100 mt-1">
                            <div className={`h-full ${barColor}`} style={{ width: `${forecast.pctRemaining}%` }} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Meta chips row: BAC, Purity, Expires */}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono-num tabular-nums">
                    <span className="text-slate-500">
                      <span className="uppercase tracking-widest text-[9px] text-slate-400 mr-1">BAC</span>
                      <span className="text-slate-700">{v.bac_water_ml != null ? `${v.bac_water_ml} mL` : "—"}</span>
                    </span>
                    <span className="text-slate-500">
                      <span className="uppercase tracking-widest text-[9px] text-slate-400 mr-1">Purity</span>
                      <span className="text-slate-700">{v.purity_pct != null ? `${v.purity_pct}%` : "—"}</span>
                    </span>
                    {hasExp && (
                    <span className={expired ? "text-[#FF3B30]" : soon ? "text-[#0D9488]" : "text-slate-500"}>
                      <span className="uppercase tracking-widest text-[9px] text-slate-400 mr-1">Expires</span>
                      <span className={expired ? "text-[#FF3B30] font-semibold" : soon ? "text-[#0D9488] font-semibold" : "text-slate-700"}>
                        {(expired || soon) && <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-0.5" />}
                        {fmtDate(v.expiration_date)}
                      </span>
                    </span>
                    )}
                  </div>

                  {canOpenNext && (
                    <Button
                      type="button"
                      onClick={() => openNextVial(v)}
                      variant="outline"
                      className="mt-3 h-9 w-full border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488]/10 rounded-none uppercase tracking-widest text-[10px] font-mono-num font-semibold"
                      data-testid={`open-next-vial-${v.id}`}
                      title={`Move to the next sealed vial — ${qty - 1} left after this`}
                    >
                      Open next vial · {qty - 1} sealed left
                    </Button>
                  )}

                  {Array.isArray(v.opens_history) && v.opens_history.length >= 1 && (
                    <details className="mt-3 group" data-testid={`vial-opens-history-${v.id}`}>
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-2 font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#0D9488] transition-colors">
                        <span className="inline-flex items-center gap-1.5">
                          <History className="w-3 h-3" />
                          Opens history · {v.opens_history.length}
                        </span>
                        <ChevronDown className="w-3 h-3 transition-transform group-open:rotate-180" />
                      </summary>
                      <ol className="mt-2 space-y-1 border-l border-slate-200 pl-3" data-testid={`vial-opens-list-${v.id}`}>
                        {[...v.opens_history].reverse().map((o, idx) => {
                          const isMostRecent = idx === 0;
                          const label =
                            o.source === "auto" ? "Auto-opened after shot"
                            : o.source === "manual" ? "Opened next vial"
                            : "Vial added";
                          const dotColor =
                            o.source === "auto" ? "bg-[#0D9488]"
                            : o.source === "manual" ? "bg-[#2563EB]"
                            : "bg-slate-400";
                          return (
                            <li key={idx} className="flex items-baseline gap-2 relative">
                              <span className={`absolute -left-[15px] top-1.5 w-2 h-2 rounded-full ${dotColor} ring-2 ring-white`} />
                              <span className="font-mono-num text-[10px] text-slate-500 tabular-nums whitespace-nowrap">
                                {fmtOpenTime(o.opened_at)}
                              </span>
                              <span className={`text-[11px] ${isMostRecent ? "text-slate-900 font-medium" : "text-slate-600"}`}>
                                {label}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
            )}
          </div>

          {/* Desktop table view */}
          <div className="hidden sm:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 hover:bg-transparent">
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Peptide</TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Amount</TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Remaining</TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    Days Left
                    <InfoTip text="Estimated days of therapy remaining at the peptide's default dose + interval. Auto-updates when you log a shot." />
                  </span>
                </TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    BAC Water
                    <InfoTip text="Volume of bacteriostatic water used to reconstitute this vial. Determines mg/mL concentration and units-per-shot on your syringe." />
                  </span>
                </TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Purity</TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Qty</TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Expires</TableHead>
                <TableHead className="text-right font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vials.map((v) => {
                const expired = isExpired(v.expiration_date);
                const soon = !expired && isExpiringSoon(v.expiration_date);
                const pepInfo = peptides.find((p) => (p.name || "").toLowerCase() === (v.peptide_name || "").toLowerCase());
                const forecast = computeDaysLeft(v, pepInfo);
                const runOutDate = forecast && forecast.days > 0 ? new Date(Date.now() + forecast.days * 86400000) : null;
                const daysColor = !forecast
                  ? "text-slate-500"
                  : forecast.days <= 0
                  ? "text-[#FF3B30]"
                  : forecast.days < 3
                  ? "text-[#FF3B30]"
                  : forecast.days < 7
                  ? "text-[#0D9488]"
                  : "text-slate-900";
                const barColor = !forecast
                  ? "bg-zinc-800"
                  : forecast.days <= 0
                  ? "bg-[#FF3B30]"
                  : forecast.days < 7
                  ? "bg-[#0D9488]"
                  : "bg-[#22C55E]";
                return (
                  <TableRow key={v.id} className="border-slate-200 hover:bg-slate-50/50 transition-colors" data-testid={`vial-row-${v.id}`}>
                    <TableCell className="font-medium text-slate-900">{v.peptide_name}</TableCell>
                    <TableCell className="font-mono-num text-slate-900">{v.amount_mg} mg</TableCell>
                    <TableCell>
                      {v.remaining_mg != null ? (
                        <div className="flex flex-col gap-1">
                          <span className={`font-mono-num text-sm ${v.remaining_mg <= 0 ? "text-[#FF3B30]" : v.remaining_mg / v.amount_mg < 0.25 ? "text-[#0D9488]" : "text-slate-900"}`}>
                            {v.remaining_mg.toFixed(2)} mg
                          </span>
                          <div className="h-1 w-20 bg-slate-50">
                            <div
                              className={`h-full ${v.remaining_mg <= 0 ? "bg-[#FF3B30]" : v.remaining_mg / v.amount_mg < 0.25 ? "bg-[#0D9488]" : "bg-[#2563EB]"}`}
                              style={{ width: `${Math.max(0, Math.min(100, (v.remaining_mg / v.amount_mg) * 100))}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`vial-days-left-${v.id}`}>
                      {!forecast ? (
                        <span className="text-slate-400 font-mono-num text-xs" title="No default dose set on this peptide (or dose in IU / mL — not convertible)">
                          n/a
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1 min-w-[92px]">
                          <div className="flex items-baseline gap-1.5">
                            <span className={`font-mono-num text-sm font-black ${daysColor}`}>
                              {formatDaysLeft(forecast.days)}
                            </span>
                            {runOutDate && forecast.days >= 0.5 && (
                              <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">
                                {runOutDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                              </span>
                            )}
                          </div>
                          <div className="h-1 w-20 bg-slate-50" title={`~${forecast.mgPerDay.toFixed(3)} mg/day · ${forecast.dosesPerDay.toFixed(2)} doses/day`}>
                            <div
                              className={`h-full ${barColor}`}
                              style={{ width: `${forecast.pctRemaining}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-num text-slate-600">{v.bac_water_ml != null ? `${v.bac_water_ml} mL` : "—"}</TableCell>
                    <TableCell className="font-mono-num text-slate-600">{v.purity_pct != null ? `${v.purity_pct}%` : "—"}</TableCell>
                    <TableCell className="font-mono-num text-slate-900">×{v.quantity}</TableCell>
                    <TableCell>
                      <span className={`font-mono-num text-sm ${expired ? "text-[#FF3B30]" : soon ? "text-[#0D9488]" : "text-slate-600"}`}>
                        {expired && <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
                        {soon && <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
                        {fmtDate(v.expiration_date)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(v)}
                          className="h-8 w-8 hover:bg-slate-200 text-slate-600 hover:text-white rounded-none"
                          data-testid={`edit-vial-${v.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(v)}
                          className="h-8 w-8 hover:bg-slate-200 text-slate-600 hover:text-[#FF3B30] rounded-none"
                          data-testid={`delete-vial-${v.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-lg" data-testid="vial-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black">
              {editing ? "Edit Vial" : "Add Vial"}
            </DialogTitle>
            <DialogDescription className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500">
              // {editing ? "update entry" : "log new vial"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="vial-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Peptide</Label>
              <Select value={form.peptide_name} onValueChange={(v) => setForm({ ...form, peptide_name: v })}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="vial-peptide">
                  <SelectValue placeholder="Select from library" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none max-h-[300px]">
                  {peptides.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Amount (mg)</Label>
                <Input required type="number" step="any" value={form.amount_mg} onChange={(e) => setForm({ ...form, amount_mg: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="vial-amount-mg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">BAC Water (mL)</Label>
                <Input type="number" step="any" value={form.bac_water_ml} onChange={(e) => setForm({ ...form, bac_water_ml: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="vial-bac-ml" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Purity %</Label>
                <Input type="number" step="any" value={form.purity_pct} onChange={(e) => setForm({ ...form, purity_pct: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="vial-purity" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Quantity</Label>
                <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="vial-quantity" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Expires</Label>
                <Input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="vial-expiration" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-slate-50 border-slate-200 rounded-none resize-none" data-testid="vial-notes" />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}
                className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800" data-testid="vial-cancel">Cancel</Button>
              <Button type="submit" disabled={saving}
                className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
                data-testid="vial-save">
                {saving ? "Saving…" : (editing ? "Update" : "Save Vial")}
              </Button>
            </DialogFooter>
          </form>
          <ProductTour steps={VIAL_TOUR_STEPS} open={tourOpen} onClose={closeTour} />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete vial?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will remove "{confirmDelete?.peptide_name}" from your inventory.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="vial-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="vial-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
